import {
  extractCollectionResults,
  pickNestedName,
  pickString,
} from "./leanCustomsRecordUtils";

export interface LeanCustomsOrderCostLine {
  label: string;
  amount: string;
}

export interface LeanCustomsOrderDocumentRow {
  filename: string;
  documentType?: string;
  byteSize?: number;
}

export interface LeanCustomsOrderDetailsDisplay {
  declarant?: string;
  importer?: string;
  agent?: string;
  shipmentReferences: string[];
  costLines: LeanCustomsOrderCostLine[];
  documents: LeanCustomsOrderDocumentRow[];
}

function pickParty(
  order: Record<string, unknown>,
  nameKeys: string[],
  idKeys: string[]
): string | undefined {
  for (const key of nameKeys) {
    const direct = pickString(order[key]);
    if (direct) return direct;
    const nested = pickNestedName(order[key]);
    if (nested) return nested;
  }
  for (const key of idKeys) {
    const nested = pickNestedName(order[key]);
    if (nested) return nested;
  }
  return undefined;
}

function parseCostLine(raw: Record<string, unknown>): LeanCustomsOrderCostLine | null {
  const costType = pickString(raw.cost_type, raw.type, raw.name, raw.label);
  const amount = pickString(
    raw.amount,
    raw.value,
    raw.total,
    raw.price,
    raw.cost_amount
  );
  const currency = pickString(raw.currency, raw.currency_code) ?? "EUR";
  if (!costType && !amount) return null;
  const label = costType ?? "Cost";
  const amountText = amount ? `${amount} ${currency}`.trim() : "—";
  return { label, amount: amountText };
}

function extractCostLines(order: Record<string, unknown>): LeanCustomsOrderCostLine[] {
  const sources = [
    order.order_costs,
    order.order_costs_attributes,
    order.costs,
    order.cost_lines,
  ];
  const lines: LeanCustomsOrderCostLine[] = [];
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const item of source) {
      if (!item || typeof item !== "object") continue;
      const parsed = parseCostLine(item as Record<string, unknown>);
      if (parsed) lines.push(parsed);
    }
  }
  return lines;
}

function extractDocumentRows(source: unknown): LeanCustomsOrderDocumentRow[] {
  if (!Array.isArray(source)) return [];
  const rows: LeanCustomsOrderDocumentRow[] = [];
  for (const item of source) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const filename = pickString(rec.filename, rec.name, rec.file_name, rec.title);
    if (!filename) continue;
    rows.push({
      filename,
      documentType: pickString(rec.document_type, rec.type, rec.kind),
      byteSize:
        typeof rec.byte_size === "number"
          ? rec.byte_size
          : typeof rec.size === "number"
            ? rec.size
            : undefined,
    });
  }
  return rows;
}

function extractOrderDocuments(order: Record<string, unknown>): LeanCustomsOrderDocumentRow[] {
  const fromOrder = [
    ...extractDocumentRows(order.documents),
    ...extractDocumentRows(order.order_documents),
    ...extractDocumentRows(order.attachments),
    ...extractDocumentRows(order.files),
  ];
  if (fromOrder.length > 0) return fromOrder;

  const shipments = order.shipments;
  if (Array.isArray(shipments)) {
    for (const shipment of shipments) {
      if (!shipment || typeof shipment !== "object") continue;
      const rec = shipment as Record<string, unknown>;
      const nested = [
        ...extractDocumentRows(rec.documents),
        ...extractDocumentRows(rec.shipment_documents),
      ];
      if (nested.length > 0) return nested;
    }
  }
  return [];
}

function shipmentReference(row: Record<string, unknown>): string | undefined {
  return pickString(
    row.reference,
    row.shipment_reference,
    row.booking_number,
    row.house_bl,
    row.master_bl,
    row.id
  );
}

export function buildLeanCustomsOrderDetailsDisplay(
  order: Record<string, unknown>,
  shipmentsPayload?: unknown
): LeanCustomsOrderDetailsDisplay {
  const shipmentRows = extractCollectionResults(shipmentsPayload);
  const embeddedShipments = Array.isArray(order.shipments)
    ? (order.shipments as Record<string, unknown>[])
    : [];
  const allShipments = shipmentRows.length > 0 ? shipmentRows : embeddedShipments;

  const shipmentReferences = [
    ...new Set(
      allShipments
        .map((row) => shipmentReference(row))
        .filter((ref): ref is string => Boolean(ref))
    ),
  ];

  const firstShipment = allShipments[0];

  return {
    declarant:
      pickParty(order, ["declarant_name"], ["declarant"]) ??
      (firstShipment
        ? pickParty(firstShipment, ["declarant_name"], ["declarant"])
        : undefined),
    importer:
      pickParty(order, ["importer_name"], ["importer"]) ??
      (firstShipment
        ? pickParty(firstShipment, ["importer_name"], ["importer"])
        : undefined),
    agent: pickParty(order, ["agent_name"], ["agent"]),
    shipmentReferences,
    costLines: extractCostLines(order),
    documents: extractOrderDocuments(order),
  };
}
